import { HttpClient } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { Observable, from, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { PushNotificationEndpoints } from '@core/constants/endpoints.const';
import {
  DEFAULT_VAPID_PUBLIC_KEY,
  IPushNotificationDto
} from '@models/notification/push-notification.model';
import { ApiResponse } from '@models/response.model';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  constructor(
    private httpClient: HttpClient,
    @Optional() private swPush: SwPush
  ) { }

  get isEnabled(): boolean {
    return this.swPush?.isEnabled ?? false;
  }

  get messages$(): Observable<any> {
    return this.swPush?.messages ?? of();
  }

  get notificationClicks$(): Observable<any> {
    return this.swPush?.notificationClicks ?? of();
  }

  get subscription$(): Observable<PushSubscription | null> {
    return this.swPush?.subscription ?? of(null);
  }

  subscribe = (
    dto: IPushNotificationDto,
    loginId: string
  ): Observable<ApiResponse<number>> => {
    return this.httpClient.post<ApiResponse<number>>(
      `${PushNotificationEndpoints.Subscribe}?loginId=${loginId}`,
      dto
    );
  };

  unsubscribe = (
    dto: IPushNotificationDto,
    loginId: string
  ): Observable<ApiResponse<number>> => {
    return this.httpClient.post<ApiResponse<number>>(
      `${PushNotificationEndpoints.Unsubscribe}?loginId=${loginId}`,
      dto
    );
  };

  toPushNotificationDto = (sub: PushSubscription): IPushNotificationDto => {
    const subJson = sub.toJSON();
    return {
      endpoint: subJson.endpoint || '',
      p256dhKey: subJson.keys ? subJson.keys['p256dh'] || '' : '',
      authKey: subJson.keys ? subJson.keys['auth'] || '' : '',
      userAgent: navigator.userAgent,
    };
  };

  /**
   * Appends the current environment base URL (window.location.origin) to relative click URLs (e.g., '/appointments')
   */
  getFullClickUrl = (clickUrl: string, baseUrl: string = window.location.origin): string => {
    if (!clickUrl) return baseUrl;
    if (clickUrl.startsWith('http://') || clickUrl.startsWith('https://')) {
      return clickUrl;
    }
    const normalizedBase = baseUrl.replace(/\/+$/, '');
    const normalizedPath = clickUrl.startsWith('/') ? clickUrl : `/${clickUrl}`;
    return `${normalizedBase}${normalizedPath}`;
  };

  /**
   * Subscribes to browser push notification click events and opens or navigates to the full clickUrl
   */
  listenToNotificationClicks = (onNavigate?: (fullUrl: string, clickUrl: string) => void) => {
    if (!this.swPush || !this.swPush.isEnabled) return;
    this.swPush.notificationClicks.subscribe((event) => {
      const rawUrl = event?.notification?.data?.url || event?.notification?.data?.clickUrl;
      if (rawUrl) {
        const fullUrl = this.getFullClickUrl(rawUrl);
        if (onNavigate) {
          onNavigate(fullUrl, rawUrl);
        } else {
          window.open(fullUrl, '_blank');
        }
      }
    });
  };

  requestSubscription = async (
    serverPublicKey: string = DEFAULT_VAPID_PUBLIC_KEY
  ): Promise<PushSubscription | null> => {
    if (!this.swPush || !this.swPush.isEnabled) {
      return null;
    }
    try {
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          return null;
        }
      }
      const subscription = await this.swPush.requestSubscription({
        serverPublicKey,
      });
      return subscription;
    } catch (error) {
      return null;
    }
  };

  subscribeAndRegister = (
    loginId: string,
    serverPublicKey: string = DEFAULT_VAPID_PUBLIC_KEY
  ): Observable<ApiResponse<number> | null> => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'denied') {
      return of(null);
    }
    return from(this.requestSubscription(serverPublicKey)).pipe(
      switchMap((sub) => {
        if (!sub) {
          return of(null);
        }
        const dto = this.toPushNotificationDto(sub);
        return this.subscribe(dto, loginId);
      }),
      catchError((err) => {
        console.error('Error during subscribeAndRegister:', err);
        return of(null);
      })
    );
  };

  unsubscribeAndUnregister = (
    loginId: string
  ): Observable<ApiResponse<number> | null> => {
    if (!this.swPush || !this.swPush.isEnabled) {
      return of(null);
    }
    return this.swPush.subscription.pipe(
      take(1),
      switchMap((sub) => {
        if (!sub) {
          return of(null);
        }
        const dto = this.toPushNotificationDto(sub);
        return this.unsubscribe(dto, loginId).pipe(
          switchMap(async (response) => {
            await this.swPush?.unsubscribe();
            return response;
          })
        );
      }),
      catchError((err) => {
        console.error('Error during unsubscribeAndUnregister:', err);
        return of(null);
      })
    );
  };
}

